import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, GripVertical } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import ImageUpload from "@/components/admin/ImageUpload";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function EntityManager({ endpoint, fields, cols, defaults, testid }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`${endpoint}/${editing.id}`, form);
      else await api.post(endpoint, form);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`${endpoint}/${id}`); load(); } catch (e) {} };
  return (
    <div>
      <div className="mb-3 flex justify-end"><Button size="sm" className="rounded-full" onClick={() => { setEditing(null); setForm({ ...defaults }); setOpen(true); }} data-testid={`${testid}-add`}><Plus className="mr-1 h-4 w-4" />Add</Button></div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((it) => (
          <div key={it.id} className="rounded-xl border border-border bg-card p-4" data-testid={`${testid}-${it.id}`}>
            {it.desktop_image || it.photo || it.image ? <img src={it.desktop_image || it.photo || it.image} alt="" className="mb-2 h-28 w-full rounded-lg object-cover" /> : null}
            <p className="font-semibold">{it.title || it.name}</p>
            <p className="text-xs text-muted-foreground">{it.subtitle || it.role || ""}</p>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => { setEditing(it); setForm(it); setOpen(true); }} data-testid={`${testid}-edit-${it.id}`}>Edit</Button>
              <Button size="sm" variant="outline" className="text-destructive" onClick={() => remove(it.id)} data-testid={`${testid}-del-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid gap-3">
            {fields.map((f) => (
              <div key={f.key}><Label className="mb-1 block text-xs">{f.label}</Label>
                {f.type === "textarea" ? <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} /> :
                 f.type === "image" ? <ImageUpload value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} testid={`${testid}-field-${f.key}`} /> :
                 f.type === "switch" ? <div className="flex h-9 items-center"><Switch checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} /></div> :
                 <Input type={f.type === "number" ? "number" : "text"} value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`${testid}-field-${f.key}`} />}
              </div>
            ))}
            <Button type="submit" className="rounded-full">Save</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CMS() {
  const [settings, setSettings] = useState(null);
  const load = () => api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const saveSettings = async (next) => {
    setSettings(next);
    try { await api.put("/settings", next); toast.success("Saved"); } catch (e) { toast.error("Save failed"); }
  };
  const toggleSection = (i) => { const arr = [...settings.sections]; arr[i] = { ...arr[i], visible: !arr[i].visible }; saveSettings({ ...settings, sections: arr }); };
  const togglePage = (i) => { const arr = [...settings.pages]; arr[i] = { ...arr[i], visible: !arr[i].visible }; saveSettings({ ...settings, pages: arr }); };
  const setPageContent = (i, content) => { const arr = [...settings.pages]; arr[i] = { ...arr[i], content }; setSettings({ ...settings, pages: arr }); };

  return (
    <AdminLayout title="Website / CMS">
      <Tabs defaultValue="hero">
        <TabsList>
          <TabsTrigger value="hero" data-testid="cms-tab-hero">Hero Slider</TabsTrigger>
          <TabsTrigger value="sections" data-testid="cms-tab-sections">Homepage Sections</TabsTrigger>
          <TabsTrigger value="pages" data-testid="cms-tab-pages">Pages</TabsTrigger>
          <TabsTrigger value="team">Team</TabsTrigger>
        </TabsList>

        <TabsContent value="hero">
          <p className="mb-2 text-xs text-muted-foreground">Recommended: Desktop 1920×640px, Mobile 800×800px. Duration controls how long each slide shows.</p>
          <EntityManager endpoint="/hero" testid="hero"
            defaults={{ is_active: true, order: 1, duration: 5, button_text: "Shop Now", button_link: "/products" }}
            fields={[
              { key: "title", label: "Title (internal only)" }, { key: "button_text", label: "Button Text" },
              { key: "button_link", label: "Button Link" }, { key: "duration", label: "Duration (seconds)", type: "number" },
              { key: "width", label: "Width (px, optional)", type: "number" }, { key: "height", label: "Height (px, optional)", type: "number" },
              { key: "desktop_image", label: "Desktop Image", type: "image" }, { key: "mobile_image", label: "Mobile Image", type: "image" },
              { key: "order", label: "Order", type: "number" }, { key: "is_active", label: "Active", type: "switch" },
            ]} />
        </TabsContent>

        <TabsContent value="team">
          <EntityManager endpoint="/team" testid="team"
            defaults={{ visible: true, order: 1 }}
            fields={[{ key: "name", label: "Name" }, { key: "role", label: "Role" }, { key: "photo", label: "Photo", type: "image" }, { key: "order", label: "Order", type: "number" }, { key: "visible", label: "Visible", type: "switch" }]} />
        </TabsContent>

        <TabsContent value="sections">
          {settings && (
            <div className="grid gap-2 rounded-xl border border-border bg-card p-4">
              {settings.sections.map((sec, i) => (
                <div key={sec.key} className="flex items-center gap-3 rounded-lg border border-border/60 px-4 py-3" data-testid={`section-${sec.key}`}>
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{sec.name}</span>
                  <span className="text-xs text-muted-foreground">order {sec.order}</span>
                  <button onClick={() => toggleSection(i)} data-testid={`section-toggle-${sec.key}`} className={`ml-auto flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${sec.visible ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
                    {sec.visible ? <><Eye className="h-3.5 w-3.5" />Visible</> : <><EyeOff className="h-3.5 w-3.5" />Hidden</>}
                  </button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="pages">
          {settings && (
            <div className="grid gap-3">
              {settings.pages.map((pg, i) => (
                <div key={pg.key} className="rounded-xl border border-border bg-card p-4" data-testid={`page-${pg.key}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-display font-semibold">{pg.name}</span>
                    <button onClick={() => togglePage(i)} data-testid={`page-toggle-${pg.key}`} className={`ml-auto flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${pg.visible ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-secondary text-muted-foreground"}`}>
                      {pg.visible ? <><Eye className="h-3.5 w-3.5" />Visible</> : <><EyeOff className="h-3.5 w-3.5" />Hidden</>}
                    </button>
                  </div>
                  <Textarea className="mt-3" value={pg.content} onChange={(e) => setPageContent(i, e.target.value)} rows={3} data-testid={`page-content-${pg.key}`} />
                  <Button size="sm" className="mt-2 rounded-full" onClick={() => saveSettings(settings)} data-testid={`page-save-${pg.key}`}>Save</Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
